/**
 * What an iPhone shows at `/portal/e/:token` BEFORE the one-time link is
 * spent (see Enrolment.jsx and installContext.js for why it waits).
 *
 *   AddToHomeFirst  real Safari: put the portal on the Home Screen first; the
 *                   new icon opens at this link and signs itself in
 *   OpenInSafari    Chrome/Firefox/in-app browser: copy the link over to Safari
 *
 * Both keep a quieter way out ("Inkább a böngészőben folytatom") that spends
 * the link right here, exactly as the Android/desktop flow does, and the
 * office's WhatsApp for help. Same dark enrolment look, same building blocks.
 *
 * THE BUILDING BLOCKS ARE SHARED, THE WORDS ARE NOT. `Steps`, `Step` and
 * `IosAddToHomeSteps` are exported because the office's own offer
 * (Components/AddToHomeOffer, English, on the dispatch board) draws the very
 * same numbered list, the same Share and "Add to Home Screen" glyphs and the
 * same app icon — from the same code, so the two can never come to describe
 * different buttons. Each caller brings its own `copy`; the driver's
 * Hungarian lives right here and nothing else may touch it.
 */
import React, { useEffect, useRef, useState } from 'react';
import BrandLogo from './BrandLogo';
import OfficeWhatsAppButton from './OfficeWhatsAppButton';
import {
  AddToHomeIcon, CheckIcon, CompassIcon, CopyIcon, PlusSquareIcon, ShareIcon,
} from './Icons';
import copyText from '../copyText';

export const INSTALL_HELP_MESSAGE = 'Szia! Elakadtam a Pickup Hungary portál főképernyőre tételénél.';

const APP_ICON_SRC = `${process.env.PUBLIC_URL || ''}/portal-apple-touch-icon.png`;

export function Steps({ children }) {
  return <ol className="pp-a2hs__steps">{children}</ol>;
}

export function Step({ number, children }) {
  return (
    <li className="pp-a2hs__step">
      <span className="pp-a2hs__num">{number}</span>
      <span className="pp-a2hs__text">{children}</span>
    </li>
  );
}

/**
 * One step that points at a button: `{ before, name, after, hint }` around a
 * drawn glyph of the thing to look for. Where the glyph sits inside the
 * sentence is the component's call, not the copy's, so both languages draw
 * it in the same place.
 */
function GlyphStep({
  number, step, glyph, glyphLast = false,
}) {
  const {
    before = '', name = '', after = '', hint = '',
  } = step || {};

  return (
    <Step number={number}>
      {before ? `${before} ` : null}
      {name ? <strong>{name}</strong> : null}
      {glyphLast ? null : glyph}
      {after ? ` ${after}` : null}
      {glyphLast ? glyph : null}
      {hint ? <span className="pp-a2hs__hint">{hint}</span> : null}
    </Step>
  );
}

/**
 * The three taps that put this page on an iPhone's Home Screen: Share, "Add
 * to Home Screen", then open the new icon.
 *
 * THE ONE COPY OF THESE STEPS. The glyphs are drawn to match what iOS really
 * shows (Icons' ShareIcon and PlusSquareIcon, and the app's own touch icon),
 * so they must not be described twice in two languages and left to drift.
 * `copy` is `{ share, add, open }`, each a `GlyphStep` step.
 */
export function IosAddToHomeSteps({ copy }) {
  return (
    <Steps>
      <GlyphStep
        number={1}
        step={copy.share}
        glyph={<ShareIcon className="pp-a2hs__glyph" size={18} />}
        glyphLast
      />
      <GlyphStep
        number={2}
        step={copy.add}
        glyph={<PlusSquareIcon className="pp-a2hs__glyph" size={18} />}
      />
      <GlyphStep
        number={3}
        step={copy.open}
        glyph={<img className="pp-a2hs__appicon" src={APP_ICON_SRC} alt="" width={22} height={22} />}
      />
    </Steps>
  );
}

/** The driver's words for those three taps — Hungarian, and only theirs. */
export const IOS_ADD_TO_HOME_STEPS = {
  share: {
    before: 'Koppints a',
    name: 'Megosztás',
    after: 'gombra',
    hint: 'Ha nem látod, előbb a ⋯ gombra koppints.',
  },
  add: {
    before: 'Görgess le, és válaszd a',
    name: '„Főképernyőhöz adás”',
    after: 'lehetőséget',
  },
  open: {
    before: 'Nyisd meg az új',
    name: '„PH Sofőr”',
    after: 'ikont. Magától bejelentkezik.',
  },
};

function Heading({ icon, title, body }) {
  return (
    <>
      <div className="pp-topbrand">
        <BrandLogo variant="white" height={48} />
      </div>
      <div className="pp-enrol pp-enrol--install">
        <div className="pp-enrol__mark">{icon}</div>
        <h1 className="pp-enrol__title">{title}</h1>
        <p className="pp-enrol__body">{body}</p>
      </div>
    </>
  );
}

function ContinueHere({ onContinue }) {
  return (
    <button type="button" className="pp-btn pp-btn--quiet pp-btn--block" onClick={onContinue}>
      Inkább a böngészőben folytatom
    </button>
  );
}

export function AddToHomeFirst({ onContinue }) {
  return (
    <>
      <Heading
        icon={<AddToHomeIcon size={38} width={2.4} />}
        title="Előbb tedd ki a főképernyőre"
        body="iPhone-on a főképernyős ikon külön jelentkezik be, nem a Safarin keresztül. Tedd ki most, és az ikon ezzel a linkkel magától belép."
      />

      <div className="pp-a2hs">
        <div className="pp-a2hs__head">
          <AddToHomeIcon className="pp-a2hs__icon" size={20} />
          <span>Így teszed ki</span>
        </div>
        <IosAddToHomeSteps copy={IOS_ADD_TO_HOME_STEPS} />
      </div>

      <div className="pp-grow" />

      <div className="pp-enrol-foot">
        <OfficeWhatsAppButton message={INSTALL_HELP_MESSAGE} />
        <ContinueHere onContinue={onContinue} />
      </div>
    </>
  );
}

export function OpenInSafari({ url, onContinue }) {
  // idle → copied (a tick for a moment) | manual (clipboard refused: show the
  // link itself, selected, to copy by hand)
  const [copy, setCopy] = useState('idle');
  const field = useRef(null);

  useEffect(() => {
    if (copy !== 'copied') return undefined;
    const timer = setTimeout(() => setCopy('idle'), 2500);
    return () => clearTimeout(timer);
  }, [copy]);

  useEffect(() => {
    if (copy === 'manual' && field.current) field.current.select();
  }, [copy]);

  const handleCopy = async () => {
    setCopy(await copyText(url) ? 'copied' : 'manual');
  };

  return (
    <>
      <Heading
        icon={<CompassIcon size={40} width={2.2} />}
        title="Nyisd meg ezt a linket Safariban"
        body="iPhone-on csak Safariból tudod úgy kitenni a főképernyőre, hogy magától bejelentkezzen."
      />

      <div className="pp-a2hs">
        <Steps>
          <Step number={1}>Másold ki a linket az alábbi gombbal</Step>
          <Step number={2}>
            Nyisd meg a
            {' '}
            <strong>Safarit</strong>
            , és illeszd be a címsorba
          </Step>
          <Step number={3}>Kövesd az ott megjelenő lépéseket</Step>
        </Steps>
      </div>

      <div className="pp-grow" />

      <div className="pp-enrol-foot">
        <button
          type="button"
          className="pp-btn pp-btn--primary pp-btn--block pp-btn--tall"
          onClick={handleCopy}
        >
          {copy === 'copied' ? <CheckIcon size={20} width={3} /> : <CopyIcon size={20} />}
          {copy === 'copied' ? 'Kimásolva' : 'Link másolása'}
        </button>
        {copy === 'manual' ? (
          <div className="pp-copyfield">
            <label className="pp-copyfield__label" htmlFor="pp-enrol-link">
              Nem sikerült kimásolni. Tartsd rajta az ujjad a linken,
              és válaszd a Másolás lehetőséget.
            </label>
            <input
              id="pp-enrol-link"
              ref={field}
              className="pp-copyfield__input"
              type="text"
              readOnly
              value={url}
              onFocus={(event) => event.target.select()}
            />
          </div>
        ) : null}
        <OfficeWhatsAppButton message={INSTALL_HELP_MESSAGE} />
        <ContinueHere onContinue={onContinue} />
      </div>
    </>
  );
}
